import * as React from 'react'
import { useService } from '@pushly/aqe/lib/hooks'
import CopyToClipboard from 'react-copy-to-clipboard'
import { FormInstance } from 'antd/es/form'
import { Button, Form, Input, Tooltip } from 'antd'
import { MinusCircleFilled, SyncOutlined, WarningOutlined } from '@ant-design/icons'
import { RuleObject } from 'antd/lib/form'
import { Modal, Well } from '@pushly/aqe/lib/components'
import { ApiKeyDto } from '@halo/dtos/user-with-key.dto'
import { AccountService } from '@halo/services/account.service'
import { simpleNotification } from '@halo/_utils/notifications'
import { classnames } from '@halo/_utils/classnames'
import '../api-settings-well/api-settings-well.scss'
import randomstring from 'randomstring'
import moment from 'moment-timezone'
import { AppService } from '@halo/services/app.service'
import { IApiCallOptions } from '@pushly/aqe/lib/interfaces'

interface IIASWProps {
    actions?: any[]
}

export interface IApiKeyCreateDto {
    name: string
    email: string
}

export const InternalApiSettingWell: React.FC<IIASWProps> = (props: IIASWProps) => {
    const accountSvc = useService(AccountService)
    const appSvc = useService(AppService)
    const [apiUsers, setApiUsers] = React.useState<ApiKeyDto[] | undefined>()
    const formRef = React.createRef<FormInstance>()

    const getApiUsers = async () => {
        const { data: accountApiUsers } = await accountSvc.fetchInternalApiUsers()

        setApiUsers(accountApiUsers)
    }

    const renderInternalApiUsers = () => {
        const displayRows: JSX.Element[] = []
        apiUsers?.forEach((api) => {
            displayRows.push(
                <ApiKeyRow
                    className={`external-row`}
                    key={`${api.name}-row`}
                    value={api}
                    triggerReload={getApiUsers}
                    handleViewableToken={handleKeyGenerateSuccess}
                />,
            )
        })

        if (displayRows.length === 0) {
            displayRows.push(
                <ApiKeyRow
                    className="no-api-row"
                    key={`no-api-key-${randomstring.generate()}`}
                    value={undefined}
                    triggerReload={getApiUsers}
                    handleViewableToken={handleKeyGenerateSuccess}
                />,
            )
        }
        return displayRows
    }

    const handleKeyGenerateSuccess = async (newToken: ApiKeyDto) => {
        Modal.success({
            className: 'api-key-success-modal',
            title: 'New API Key',
            icon: <WarningOutlined />,
            okText: 'Got it',
            content: (
                <div className="api-key-success-modal-content">
                    <div>
                        <span>
                            The following API Key has been generated. Please copy and/or save this key. Once this window
                            is closed you will not be able to get this key back. A new one must be generated and then
                            this key will be deactivated.
                        </span>
                    </div>
                    <div className="api-key-viewable">
                        <CopyToClipboard
                            text={newToken?.token!}
                            onCopy={() => simpleNotification('success', 'Your API Key has been copied!')}
                        >
                            <span>{newToken.token}</span>
                        </CopyToClipboard>
                    </div>
                </div>
            ),
        })
    }

    const handleCreateAPIKey = async () => {
        const createKey = async (dto: IApiKeyCreateDto, opts?: IApiCallOptions) => {
            const res = await appSvc.createApiKey(dto, opts)
            if (res.ok) {
                handleKeyGenerateSuccess(res.data)
                await getApiUsers()
                return { ok: res.ok }
            }
        }
        Modal.confirm({
            title: 'Create API Key',
            icon: <WarningOutlined />,
            okText: 'Create',
            onOk: async (close) => {
                const form = formRef.current

                if (form) {
                    const valid = await form.validateFields()

                    if (valid) {
                        const createDto = {
                            name: form.getFieldValue('name'),
                            email: form.getFieldValue('email'),
                        }

                        const res = await createKey(createDto)

                        if (res?.ok) {
                            return true
                        }
                    }
                }
            },
            content: (
                <Form ref={formRef}>
                    <p>Please provide the name of the API Key you wish to create and the associated Pushly Email.</p>

                    <Form.Item
                        name="name"
                        label="Name"
                        validateTrigger={false}
                        rules={[
                            {
                                required: true,
                                message: 'Name is required.',
                            },
                        ]}
                    >
                        <Input autoFocus={true} />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            {
                                required: true,
                                validator: (_rule, value, error) => {
                                    if (!value?.trim()) {
                                        return Promise.reject('Email is required.')
                                    }

                                    const validValue = /.+@(pushly)\.com$/.test(value?.trim())

                                    if (!validValue) {
                                        return Promise.reject('Email must be an "@pushly.com" address.')
                                    } else {
                                        return Promise.resolve(true)
                                    }
                                },
                            },
                        ]}
                    >
                        <Input autoFocus={false} />
                    </Form.Item>
                </Form>
            ),
        })
    }

    React.useEffect(() => {
        if (!apiUsers) {
            getApiUsers()
        }
    }, [apiUsers])

    const action = (
        <Button key="new-api-key" size="small" onClick={handleCreateAPIKey}>
            Create API Key
        </Button>
    )

    function render(): React.ReactElement {
        return (
            <Well
                actions={action}
                title={
                    <div>
                        <div>Internal API Key{`${apiUsers?.length! > 1 ? 's' : ''}`}</div>
                    </div>
                }
                hideFooter={true}
                className="api-well"
            >
                {apiUsers && (
                    <>
                        <div className="last-used-at-header">
                            <span>Last Used At:</span>
                        </div>
                        {renderInternalApiUsers()}
                    </>
                )}
            </Well>
        )
    }

    return render()
}

interface IApiKeyRowProps {
    className: string
    value: ApiKeyDto | undefined
    triggerReload: () => void
    handleViewableToken: (key: ApiKeyDto) => void
}

const ApiKeyRow: React.FC<IApiKeyRowProps> = (props: IApiKeyRowProps) => {
    const accountSvc = useService(AccountService)
    const appSvc = useService(AppService)
    const formRef = React.createRef<FormInstance>()
    const { value, className } = props

    const [apiKey, setApiKey] = React.useState<ApiKeyDto | undefined>(value)
    const [isGeneratingKey, setIsGeneratingKey] = React.useState<boolean>(false)

    const regenerateKey = async () => {
        if (isGeneratingKey) {
            return
        }
        setIsGeneratingKey(true)

        const res = await accountSvc.regenerateInternalApiKey(apiKey?.id)
        if (res.ok) {
            setApiKey(res.data)

            return props.handleViewableToken(res.data)
        }

        setIsGeneratingKey(false)
    }

    const handleRegenClick = async () => {
        Modal.confirm({
            title: 'Regenerate API Key',
            icon: <WarningOutlined />,
            okText: 'Regenerate',
            onOk: async (close) => {
                const form = formRef.current

                if (form) {
                    await form.validateFields()
                    await regenerateKey()
                }

                return true
            },
            content: (
                <Form ref={formRef}>
                    <p>
                        Are you sure you want to revoke the current API token? Once revoked the current token will no
                        longer be valid and any existing API calls using it will need to be updated to use the newly
                        generated token.
                    </p>

                    <p>
                        Please type, <b>REGENERATE</b>, to continue with this action.
                    </p>

                    <Form.Item
                        name="name"
                        validateTrigger={false}
                        rules={[
                            {
                                validator: (_rule: RuleObject, val, error) => {
                                    if (!val?.trim()) {
                                        return Promise.reject(`Regenerate is required.`)
                                    } else if (val !== 'REGENERATE') {
                                        return Promise.reject(`You must type "Regenerate" to continue.`)
                                    } else {
                                        return Promise.resolve(true)
                                    }
                                },
                            },
                        ]}
                    >
                        <Input autoFocus={true} />
                    </Form.Item>
                </Form>
            ),
        })
    }

    const handleArchiveKey = async () => {
        ;async (dto: IApiKeyCreateDto, opts?: IApiCallOptions) => appSvc.createApiKey(dto, opts)
        const archive = async (apiKeyId: string, opts?: IApiCallOptions) => {
            const res = await appSvc.archiveApiKey(apiKeyId, opts)
            if (res.ok) {
                props.triggerReload()
            }
        }
        Modal.confirm({
            title: 'Archive API Key',
            icon: <WarningOutlined />,
            okText: 'Archive',
            onOk: async (close) => {
                const form = formRef.current

                if (form) {
                    const valid = await form.validateFields()

                    if (valid) {
                        await archive(apiKey.id)
                    }
                }

                return true
            },
            content: (
                <Form ref={formRef}>
                    <p>
                        Are you sure you want to archive the current API token? Once archived the current token will no
                        longer be valid and any existing API calls using it will no longer be authorized.
                    </p>

                    <p>
                        Please type, <b>ARCHIVE</b>, to continue with this action.
                    </p>

                    <Form.Item
                        name="name"
                        validateTrigger={false}
                        rules={[
                            {
                                validator: (_rule: RuleObject, val, error) => {
                                    if (!val?.trim()) {
                                        return Promise.reject(`ARCHIVE is required.`)
                                    } else if (val !== 'ARCHIVE') {
                                        return Promise.reject(`You must type "ARCHIVE" to continue.`)
                                    } else {
                                        return Promise.resolve(true)
                                    }
                                },
                            },
                        ]}
                    >
                        <Input autoFocus={true} />
                    </Form.Item>
                </Form>
            ),
        })
    }

    function render() {
        const apiKeyLastUsed = apiKey?.lastUsedAt ? moment(apiKey?.lastUsedAt).format('LL') : 'N/A'

        return (
            <div className={className}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>
                        {apiKey ? apiKey?.name ?? 'API Key' : <span>No Active API Keys</span>}
                    </div>
                    <div className={classnames('form-layout-row-value')}>
                        <div className="api-key-field">
                            {apiKey && (
                                <div className="api-key-field-value">
                                    <div>
                                        <Input
                                            size="small"
                                            value={randomstring.generate(64)}
                                            type="password"
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="api-key-field-actions">
                                <Button size="small" onClick={() => handleRegenClick()}>
                                    {apiKey ? (
                                        <Tooltip title="Regenerate Key">
                                            <SyncOutlined />
                                        </Tooltip>
                                    ) : (
                                        'Generate'
                                    )}
                                </Button>
                                <Button size="small" onClick={() => handleArchiveKey()}>
                                    {apiKey && (
                                        <Tooltip title="Archive Key">
                                            <MinusCircleFilled />
                                        </Tooltip>
                                    )}
                                </Button>
                            </div>
                            <div className="api-key-field-date">{apiKeyLastUsed && <span>{apiKeyLastUsed}</span>}</div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return render()
}
